// @ts-nocheck
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button, Checkbox, Input, InputNumber, Select, App, Divider, Typography,
} from "./admin-ui";
import {
  ArrowLeftOutlined,
  SafetyCertificateOutlined,
  WarningFilled,
  InfoCircleOutlined,
  AlignLeftOutlined,
  CreditCardOutlined,
} from "./admin-icons";
import { formatDateTimeDetail } from "../utils/formatLocaleDate";
import { formatTransactionId } from "../utils/transactionUi";
import { useInitiateRefund } from "../api/hooks";

const { Text } = Typography;

const ISSUE_PAYMENT_I18N: Record<string, { title: string; desc: string }> = {
  visa: { title: "issueRefund.payVisa", desc: "issueRefund.descVisa" },
  fpx: { title: "issueRefund.payFpx", desc: "issueRefund.descFpx" },
  grabpay: { title: "issueRefund.payGrab", desc: "issueRefund.descGrab" },
  cash: { title: "issueRefund.payCash", desc: "issueRefund.descCash" },
  ewallet: { title: "issueRefund.payEwallet", desc: "issueRefund.descEwallet" },
  tng: { title: "issueRefund.payTng", desc: "issueRefund.descTng" },
  boost: { title: "issueRefund.payBoost", desc: "issueRefund.descBoost" },
};

export function IssueRefundForm({
  txn,
  onBack,
  onOrderClick,
  onSuccess,
  embedded = false,
}: {
  txn: any;
  onBack: () => void;
  onOrderClick?: (orderId: string) => void;
  onSuccess?: () => void;
  embedded?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const initiateRefund = useInitiateRefund();

  const reasonOptions = useMemo(
    () => [
      { value: "Customer Cancelled", label: t("issueRefund.reasonCustomerCancelled") },
      { value: "Out of stock", label: t("issueRefund.reasonOutOfStock") },
      { value: "Wrong item", label: t("issueRefund.reasonWrongItem") },
      { value: "Duplicate payment", label: t("issueRefund.reasonDuplicate") },
      { value: "Order not delivered", label: t("issueRefund.reasonNotDelivered") },
      { value: "Quality issue", label: t("issueRefund.reasonQuality") },
      { value: "Other", label: t("issueRefund.reasonOther") },
    ],
    [t],
  );

  const [refundType, setRefundType] = useState<"full" | "partial">("full");
  const [partialAmount, setPartialAmount] = useState<number | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [internalNote, setInternalNote] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const order = txn.order;
  const txnTotal = Number(txn.amount ?? 0);
  const refundAmount = refundType === "full" ? txnTotal : (partialAmount ?? 0);
  const overLimit = refundAmount > txnTotal + 0.004;   // the input's own max is not enforced, so check here
  const canSubmit = !!reason && confirmed && refundAmount > 0 && !overLimit && !!order?.id;
  const txnDisplayCode = formatTransactionId(txn.id);
  const payCopy = ISSUE_PAYMENT_I18N[txn.method] ?? null;
  const payTitle = payCopy ? t(payCopy.title) : txn.method.toUpperCase();
  const payDesc = payCopy ? t(payCopy.desc) : t("issueRefund.methodDescFallback");

  const submit = async () => {
    if (!order?.id || !reason) return;
    try {
      await initiateRefund.mutateAsync({
        orderId: order.id,
        amount: refundAmount.toFixed(2),
        type: refundType,
        reason,
        method: txn.method,
      });
      void message.success(t("issueRefund.success"));
      onSuccess?.();
    } catch {
      void message.error(t("issueRefund.failed"));
    }
  };

  return (
    <div className={embedded ? "bg-transparent" : "admin-detail-page admin-refund-form min-h-screen bg-[#f8f9fa] px-8 pb-7 pt-[76px]"}>
      <div className={embedded ? "w-full" : "mx-auto max-w-[1280px]"}>
        <div className={embedded ? "mb-6 flex items-center justify-between gap-3 border-b border-[#e5e7eb] bg-transparent pb-4" : "admin-detail-topbar fixed right-0 top-16 z-40 flex items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-8 py-3.5 shadow-[0_8px_20px_rgba(25,28,29,0.03)] left-64 max-[900px]:left-[76px] max-[900px]:px-4 min-[901px]:max-[1280px]:left-[220px] min-[901px]:max-[1280px]:px-5"}>
          <div className="flex min-w-0 items-center gap-3">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              className="!h-10 !w-10 !rounded-xl !border-none !bg-transparent !p-0 !text-[#534434]"
            />
            <div className="min-w-0">
              <div className="truncate text-xs font-bold uppercase tracking-[0.8px] text-[#867461]">{t("issueRefund.title")}</div>
              <div className="flex items-center gap-2">
                <div className="truncate text-base font-black text-[#191c1d]">{txnDisplayCode}</div>
                {order && (
                  <button
                    type="button"
                    onClick={() => onOrderClick?.(order.id)}
                    className="rounded-full border border-[#ffddb8] bg-[#fff7ed] px-2 py-0.5 text-[10px] font-black text-[#ea580c]"
                  >
                    #{order.order_code}
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] font-black uppercase tracking-[0.8px] text-[#867461]">{t("issueRefund.datePaid")}</div>
            <div className="text-sm font-black text-[#ea580c]">{formatDateTimeDetail(txn.paid_at ?? txn.created_at, i18n.language)}</div>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="flex min-w-0 flex-col gap-5">
            <SectionCard title={t("issueRefund.sectionRefundType")} icon="↺">
              <div className="grid gap-4 sm:grid-cols-2">
                <RefundTypeCard
                  selected={refundType === "full"}
                  title={t("issueRefund.fullRefund")}
                  description={t("issueRefund.fullRefundDesc", { amount: txnTotal.toFixed(2) })}
                  onClick={() => setRefundType("full")}
                />
                <RefundTypeCard
                  selected={refundType === "partial"}
                  title={t("issueRefund.partialRefund")}
                  description={t("issueRefund.partialDesc")}
                  onClick={() => setRefundType("partial")}
                >
                  {refundType === "partial" && (
                    <div className="mt-3" onClick={(event) => event.stopPropagation()}>
                      <InputNumber
                        prefix="RM"
                        min={0.01}
                        max={txnTotal}
                        precision={2}
                        placeholder="0.00"
                        value={partialAmount}
                        onChange={value => setPartialAmount(value)}
                        style={{ width: "100%" }}
                      />
                      <div className={`mt-2 text-[11px] font-bold ${overLimit ? "text-[#b91c1c]" : "text-[#867461]"}`}>
                        {overLimit ? t("issueRefund.overLimit", { amount: txnTotal.toFixed(2) }) : t("issueRefund.maxRefundable", { amount: txnTotal.toFixed(2) })}
                      </div>
                    </div>
                  )}
                </RefundTypeCard>
              </div>
            </SectionCard>

            {order && (
              <SectionCard title={t("issueRefund.sectionBreakdown")} icon="≡">
                <div className="flex flex-col gap-3">
                  {refundType === "full" ? (
                    <>
                      <BreakdownRow label={t("issueRefund.foodBev")} amount={Number(order.subtotal)} />
                      {Number(order.delivery_fee) > 0 && <BreakdownRow label={t("issueRefund.deliveryFee")} amount={Number(order.delivery_fee)} />}
                      {Number(order.sst_amount) > 0 && (
                        <BreakdownRow
                          label={t("issueRefund.sst", { rate: Number(order.subtotal) > 0 ? Math.round((Number(order.sst_amount) / Number(order.subtotal)) * 100) : 0 })}
                          amount={Number(order.sst_amount)}
                        />
                      )}
                      {order.voucher_code && Number(order.voucher_discount) > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="font-semibold text-[#15803d]">{t("issueRefund.appliedDiscount")}</span>
                          <span className="font-black text-[#15803d]">-RM {Number(order.voucher_discount).toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <BreakdownRow label={t("issueRefund.refundAmountRow")} amount={partialAmount ?? 0} />
                  )}
                </div>
                <Divider style={{ margin: "18px 0" }} />
                <div className="flex items-center justify-between rounded-2xl bg-[#fff7ed] px-4 py-3">
                  <span className="text-sm font-black text-[#534434]">{t("issueRefund.totalRefundAmount")}</span>
                  <span className="text-lg font-black text-[#ea580c]">RM {refundAmount.toFixed(2)}</span>
                </div>
              </SectionCard>
            )}

            <SectionCard title={t("issueRefund.sectionReason")} icon={<AlignLeftOutlined className="text-sm" />}>
              <div className="grid gap-4">
                <FieldLabel label={t("issueRefund.refundReasonLabel")}>
                  <Select
                    placeholder={t("issueRefund.reasonPlaceholder")}
                    value={reason}
                    onChange={setReason}
                    options={reasonOptions}
                  />
                </FieldLabel>
                <FieldLabel label={t("issueRefund.internalNote")} optional={t("issueRefund.internalOptional")}>
                  <Input.TextArea
                    rows={4}
                    placeholder={t("issueRefund.internalPlaceholder")}
                    value={internalNote}
                    onChange={event => setInternalNote(event.target.value)}
                    className="!resize-none"
                  />
                </FieldLabel>
              </div>
            </SectionCard>

            <SectionCard title={t("issueRefund.sectionMethod")} icon={<CreditCardOutlined className="text-sm" />}>
              <div className="flex items-start gap-4 rounded-2xl border border-[#e7e1db] bg-[#f8f9fa] p-4">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#006c49] text-white">
                  <SafetyCertificateOutlined className="text-xl" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-black text-[#191c1d]">{payTitle}</span>
                    <span className="size-2 rounded-full bg-[#16a34a]" />
                  </div>
                  <div className="mt-1 text-xs font-medium leading-5 text-[#534434]">{payDesc}</div>
                </div>
              </div>
            </SectionCard>
          </div>

          <aside className="flex flex-col gap-4 lg:sticky lg:top-24">
            <div className="rounded-3xl border border-[#eee8e2] bg-white p-5 text-right shadow-[0_12px_28px_rgba(25,28,29,0.04)]">
              <div className="text-[11px] font-black uppercase tracking-[0.8px] text-[#867461]">{t("issueRefund.originalPaid")}</div>
              <div className="mt-2 text-3xl font-black text-[#191c1d]">RM {txnTotal.toFixed(2)}</div>
            </div>

            <div className="rounded-3xl bg-[#2f2118] p-5 shadow-[0_14px_34px_rgba(25,28,29,0.12)]">
              <div className="text-[11px] font-black uppercase tracking-[0.8px] text-[#d6c4b5]">{t("issueRefund.settlementSummary")}</div>
              <div className="mt-3 text-3xl font-black text-white">RM {refundAmount > 0 ? refundAmount.toFixed(2) : "0.00"}</div>

              <div className="mt-5 flex items-start gap-3 rounded-2xl border border-[#7f1d1d] bg-[#3b1a1a] p-3">
                <WarningFilled className="mt-0.5 shrink-0 text-[#f87171]" />
                <div className="text-xs font-medium leading-5 text-[#fca5a5]">{t("issueRefund.warningIrreversible")}</div>
              </div>

              <label className="mt-5 flex cursor-pointer items-start gap-3">
                <Checkbox checked={confirmed} onChange={event => setConfirmed(event.target.checked)} />
                <span className="text-xs font-medium leading-5 text-[#d6c4b5]">{t("issueRefund.confirmCheckbox")}</span>
              </label>

              <Button
                block
                type="primary"
                loading={initiateRefund.isPending}
                disabled={!canSubmit}
                onClick={() => void submit()}
                className={`mt-5 !h-12 !rounded-xl !border-none !font-black ${
                  canSubmit ? "!bg-[#dc2626] !text-white hover:!bg-[#b91c1c]" : "!bg-[#6f7477] !text-white"
                }`}
              >
                {t("issueRefund.confirmRefund")}
              </Button>

              <button
                type="button"
                onClick={onBack}
                className="mt-4 w-full border-none bg-transparent text-center text-xs font-bold text-[#d6c4b5] underline"
              >
                {t("issueRefund.cancelReturn")}
              </button>

              {order && (
                <div className="mt-5 border-t border-white/10 pt-4 text-[11px] font-medium leading-5 text-[#a6978b]">
                  <div>{t("issueRefund.merchantId")} KPI-{txn.id.slice(0, 7).toUpperCase()}</div>
                  <div>{t("issueRefund.authorizedBy")} {t("issueRefund.roleAdmin")}</div>
                </div>
              )}
            </div>

            {refundType === "partial" && (
              <div className="flex items-start gap-3 rounded-2xl border border-[#bfdbfe] bg-[#eff6ff] p-4">
                <InfoCircleOutlined className="mt-0.5 shrink-0 text-[#2563eb]" />
                <div>
                  <div className="text-xs font-black text-[#1d4ed8]">{t("issueRefund.partialTipTitle")}</div>
                  <div className="mt-1 text-xs font-medium leading-5 text-[#1e40af]">{t("issueRefund.partialTipBody")}</div>
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function MetaChip({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#f3f4f5] px-3 py-1.5">
      <span className="font-bold text-[#867461]">{label}</span>
      <span className="font-black text-[#191c1d]">{value}</span>
    </span>
  );
}

function SectionCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-3xl border border-[#eee8e2] bg-white p-6 shadow-[0_12px_28px_rgba(25,28,29,0.04)]">
      <SectionHeader icon={icon} label={title} />
      <div className="mt-5">{children}</div>
    </section>
  );
}

function FieldLabel({ label, optional, children }: { label: string; optional?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-2 text-[11px] font-black uppercase tracking-[0.7px] text-[#534434]">
        {label}
        {optional && <span className="ml-1 font-semibold text-[#a8a29e]">{optional}</span>}
      </div>
      {children}
    </label>
  );
}

function RefundTypeCard({ selected, title, description, onClick, children }: {
  selected: boolean;
  title: string;
  description: string;
  onClick: () => void;
  children?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-[128px] rounded-2xl border-2 p-5 text-left transition ${
        selected ? "border-[#ea580c] bg-[#fff7ed]" : "border-[#eee8e2] bg-white hover:border-[#fed7aa] hover:bg-[#fffaf5]"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-base font-black text-[#191c1d]">{title}</div>
          <div className="mt-2 text-sm font-medium leading-5 text-[#534434]">{description}</div>
        </div>
        <RadioDot selected={selected} />
      </div>
      {children}
    </button>
  );
}

function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span className={`mt-1 flex size-[18px] shrink-0 items-center justify-center rounded-full border-2 ${selected ? "border-[#ea580c]" : "border-[#d6c4b5]"}`}>
      {selected && <span className="size-2 rounded-full bg-[#ea580c]" />}
    </span>
  );
}

function SectionHeader({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-black text-[#ea580c]">{icon}</span>
      <span className="text-[11px] font-black uppercase tracking-[0.8px] text-[#534434]">{label}</span>
    </div>
  );
}

function BreakdownRow({ label, amount }: { label: string; amount: number }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="font-semibold text-[#534434]">{label}</span>
      <span className="font-black text-[#191c1d]">RM {amount.toFixed(2)}</span>
    </div>
  );
}
